// @flow
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import debounce from 'lodash.debounce';
import {
  Card,
  Table,
  Row,
  Col,
  Input,
} from 'antd';
import { SpH5 } from 'components/DesignKit';
import { useLocation } from 'react-router-dom';

import Loading from 'components/Loading';
import formatNumber from 'utils/formatNumber';
import CheckboxMenu from 'utils/checkboxMenu';
import AmountMenu from 'utils/amountMenu';
import DateMenu from 'utils/dateMenu';
import { API_END_POINTS, axiosAuthInstance } from 'api';

import {
  PAGES,
  MESSAGES,
} from 'appconstants';

import {
  reportingTransactions,
  filterParamsActions,
  notificationActions,
} from 'store/actions';
import { FILTERS_SPECS, NOTIFICATION } from 'store/actionTypes';
import moment from 'moment-timezone';
import 'assets/css/reportingPageStyle.css';

type Props = {
  fetchTransactionsReporting: Function,
  transactionsReport: Array<Object>,
  loading: boolean,
  test: boolean,
  history: {
    push: Function,
  },
  selectedAccount: Object,
  setNotification: Function,
};

const TransactinsReporting = (props: Props) => {
  const {
    loading,
    fetchTransactionsReporting,
    transactionsReport,
    test,
    history,
    selectedAccount,
    setNotification,
  } = props;

  const location = useLocation();
  const [searchMethos, setSearchMethos] = useState('');
  const [searchStatus, setSearchStatus] = useState('');
  const [searchCats, setSearchCats] = useState('');
  const [selectedM, setSelectedM] = useState([]);
  const [selectedS, setSelectedS] = useState([]);
  const [selectedC, setSelectedC] = useState([]);
  const currencyType = selectedAccount && selectedAccount.currency;
  const [searchValue, setSearchValue] = useState('');
  const [filterdRecods, setFilterdRecords] = useState([]);
  const [transactionCategory, setTransactionCategory] = useState([]);
  const [searchAmount, setSearchAmount] = useState('');
  const [searchDate, setSearchDate] = useState('');

  const debouncedFetchTransactions = debounce(fetchTransactionsReporting, 2000, { leading: true });

  const getData = (query) => {
    debouncedFetchTransactions({ query });
  };

  const getCategories = async () => {
    const { gid } = selectedAccount;
    try {
      const { data: { entity } } = await axiosAuthInstance.get(`${API_END_POINTS.DOCUSIGN}/${gid}/supported-payment-category`);
      setTransactionCategory(entity);
    } catch (e) {
      setNotification({
        type: NOTIFICATION.ERROR,
        payload: e.response ? e.response.data.message : MESSAGES.API_ERROR,
      });
    }
  };

  useEffect(() => {
    if (location.search) {
      getData(location.search);
    }
  }, [location]);

  useEffect(() => {
    const tz = selectedAccount?.timezone;
    const format = 'YYYY-MM-DDTHH:mm:ss';
    const currentEndDate = moment()
      .tz(tz);
    const currentStartDate = moment(currentEndDate)
      .tz(tz)
      .startOf('day');
    const end = moment(currentEndDate).utc().format(format);
    const start = moment(currentStartDate).utc().format(format);
    getData(`?transactionDate.AF=${start}&transactionDate.BF=${end}`);
    getCategories();
  }, [test, selectedAccount]);

  useEffect(() => {
    setFilterdRecords(transactionsReport);
    setSearchValue('');
  }, [transactionsReport]);

  const tableHeader = (title) => (
    <span
      style={{
        fontWeight: 'bold',
        fontSize: '14px',
        color: '#333333',
      }}
    >
      {title}
    </span>
  );

  const columns = [
    {
      title: tableHeader('TRANSACTION #'),
      dataIndex: 'gid',
      width: '17.5%',
      align: 'left',
      render: (gid) => (
        <span style={{ cursor: 'pointer' }}>
          {gid || <>&#8211;</>}
        </span>
      ),
    },
    {
      title: tableHeader('NAME'),
      dataIndex: 'customerName',
      width: '10%',
      align: 'left',
      render: (cname) => (
        <span style={{ cursor: 'pointer' }}>
          {cname || <>&#8211;</>}
        </span>
      ),
    },
    {
      title: tableHeader('DATE'),
      dataIndex: 'paymentDate',
      width: '10%',
      align: 'left',
      sorter: false,
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: 'descend',
      render: (paymentDate) => {
        if (paymentDate && selectedAccount && selectedAccount.timezone) {
          const tzDate = moment
            .utc(paymentDate)
            .tz(selectedAccount.timezone)
            .format('MMM DD, YYYY, hh:mm a');
          return (<span>{tzDate}</span>);
        }
        return (
          <>&#8211;</>
        );
      },
    },
    {
      title: tableHeader('CATEGORY'),
      dataIndex: 'category',
      width: '10%',
      align: 'left',
      render: (category) => (
        <span style={{ cursor: 'pointer' }}>
          {category || <>&#8211;</>}
        </span>
      ),
    },
    {
      title: tableHeader('METHOD'),
      dataIndex: 'type',
      width: '10%',
      align: 'left',
      render: (type) => (
        <span style={{ cursor: 'pointer' }}>
          {type || <>&#8211;</>}
        </span>
      ),
    },
    {
      title: tableHeader('BRAND'),
      dataIndex: 'brand',
      width: '7.5%',
      align: 'left',
      render: (brand) => (
        <span style={{ cursor: 'pointer' }}>
          {brand || <>&#8211;</>}
        </span>
      ),
    },
    {
      title: tableHeader('LAST 4'),
      dataIndex: 'lastFour',
      width: '7.5%',
      align: 'left',
      render: (lastFour) => (
        <span style={{ cursor: 'pointer' }}>
          {lastFour || <>&#8211;</>}
        </span>
      ),
    },
    {
      title: tableHeader('AMOUNT'),
      dataIndex: 'amount',
      width: '10%',
      align: 'left',
      render: (amount) => {
        const total = (amount / 100).toFixed(2);
        return (
          <span style={{ cursor: 'pointer' }}>
            {`${selectedAccount.currency.prefix} ${formatNumber(total, currencyType)}`}
          </span>
        );
      },
    },
    {
      title: tableHeader('AUTH CODE'),
      dataIndex: 'authCode',
      width: '7.5%',
      align: 'left',
      render: (authCode) => (
        <span style={{ cursor: 'pointer' }}>
          {authCode || <>&#8211;</>}
        </span>
      ),
    },
    {
      title: tableHeader('STATUS'),
      dataIndex: 'paymentSessionStatus',
      width: '10%',
      align: 'left',
      render: (paymentSessionStatus) => (
        <span style={{ cursor: 'pointer' }}>
          {paymentSessionStatus || <>&#8211;</>}
        </span>
      ),
    },
  ];

  const transactionMethods = [
    'CHARGE',
    'REFUND',
    'ADJUSTMENT',
    'PAYOUT',
    'FORCE_CREDIT',
  ];

  const transactionStatus = [
    'REQUIRE_PAYMENT_METHOD',
    'REQUIRE_CONFIRMATION',
    'REQUIRE_ACTION',
    'PROCESSING',
    'REQUIRE_CAPTURE',
    'SUCCEEDED',
    'CANCELLED',
    'FAILED',
    'DECLINED',
    'REFUND_PROCESSING',
    'PARTIAL_REFUNDED',
    'REFUNDED',
    'DISPUTED_NEEDS_RESPONSE',
    'DISPUTE_UNDER_REVIEW',
    'DISPUTE_WON',
    'DISPUTE_LOST',
    'REVERSED',
  ];

  useEffect(() => {
    if (searchMethos && !searchStatus && !searchCats && !searchAmount && !searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchMethos}`,
      });
    }
    if (!searchMethos && searchStatus && !searchCats && !searchAmount && !searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchStatus}`,
      });
    }
    if (!searchMethos && !searchStatus && searchCats && !searchAmount && !searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchCats}`,
      });
    }
    if (!searchMethos && !searchStatus && !searchCats && searchAmount && !searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchAmount}`,
      });
    }
    if (!searchMethos && !searchStatus && !searchCats && !searchAmount && searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchDate}`,
      });
    }
    if (searchMethos && searchStatus && searchCats && searchAmount && searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchMethos}&${searchStatus}&${searchCats}&${searchAmount}&${searchDate}`,
      });
    }
    if (searchMethos && searchStatus && !searchCats && !searchAmount && !searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchMethos}&${searchStatus}`,
      });
    }
    if (searchMethos && searchStatus && !searchCats && !searchAmount && !searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchMethos}&${searchStatus}`,
      });
    }
    if (searchMethos && !searchStatus && searchCats && !searchAmount && !searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchMethos}&${searchCats}`,
      });
    }
    if (searchMethos && !searchStatus && !searchCats && searchAmount && !searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchMethos}&${searchAmount}`,
      });
    }
    if (searchMethos && !searchStatus && !searchCats && !searchAmount && searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchMethos}&${searchDate}`,
      });
    }
    if (!searchMethos && searchStatus && searchCats && !searchAmount && !searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchStatus}&${searchCats}`,
      });
    }
    if (!searchMethos && searchStatus && !searchCats && searchAmount && !searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchStatus}&${searchAmount}`,
      });
    }
    if (!searchMethos && searchStatus && !searchCats && !searchAmount && searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchStatus}&${searchDate}`,
      });
    }
    if (!searchMethos && !searchStatus && searchCats && searchAmount && !searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchCats}&${searchAmount}`,
      });
    }
    if (!searchMethos && !searchStatus && searchCats && !searchAmount && searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchCats}&${searchDate}`,
      });
    }
    if (!searchMethos && !searchStatus && !searchCats && searchAmount && searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchAmount}&${searchDate}`,
      });
    }
    if (searchMethos && searchStatus && searchCats && !searchAmount && !searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchMethos}&${searchStatus}&${searchCats}`,
      });
    }
    if (searchMethos && searchStatus && !searchCats && searchAmount && !searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchMethos}&${searchStatus}&${searchAmount}`,
      });
    }
    if (searchMethos && searchStatus && !searchCats && !searchAmount && searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchMethos}&${searchStatus}&${searchDate}`,
      });
    }
    if (searchMethos && !searchStatus && searchCats && !searchAmount && searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchMethos}&${searchCats}&${searchDate}`,
      });
    }
    if (searchMethos && !searchStatus && !searchCats && searchAmount && searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchMethos}&${searchAmount}&${searchDate}`,
      });
    }
    if (!searchMethos && searchStatus && searchCats && searchAmount && !searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchStatus}&${searchCats}&${searchAmount}`,
      });
    }
    if (!searchMethos && searchStatus && searchCats && !searchAmount && searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchStatus}&${searchCats}&${searchDate}`,
      });
    }
    if (!searchMethos && searchStatus && !searchCats && searchAmount && searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchStatus}&${searchAmount}&${searchDate}`,
      });
    }
    if (!searchMethos && !searchStatus && searchCats && searchAmount && searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchCats}&${searchAmount}&${searchDate}`,
      });
    }
    if (searchMethos && searchStatus && searchCats && searchAmount && !searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchMethos}&${searchStatus}&${searchCats}&${searchAmount}`,
      });
    }
    if (searchMethos && searchStatus && searchCats && !searchAmount && searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchMethos}&${searchStatus}&${searchCats}&${searchDate}`,
      });
    }
    if (searchMethos && searchStatus && !searchCats && searchAmount && searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchMethos}&${searchStatus}&${searchAmount}&${searchDate}`,
      });
    }
    if (searchMethos && !searchStatus && searchCats && searchAmount && searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchMethos}&${searchCats}&${searchAmount}&${searchDate}`,
      });
    }
    if (!searchMethos && searchStatus && searchCats && searchAmount && searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: `?${searchStatus}&${searchCats}&${searchAmount}&${searchDate}`,
      });
    }
    if (!searchMethos && !searchStatus && !searchCats && !searchAmount && !searchDate) {
      history.push({
        pathname: PAGES.REPORTING_TRANSACTIONS,
        search: '',
      });
    }
  }, [searchMethos, searchStatus, searchCats, searchAmount, searchDate]);

  const onMethodChange = (selection) => {
    const searchMethod = [];
    for (let i = 0; i < selection.length; i += 1) {
      searchMethod.push(`${'transactionTypes'}=${selection[i]}`);
    }
    setSearchMethos(searchMethod.toString().replaceAll(',', '&'));
    setSelectedM(selection);
  };

  const onStatusChange = (selection) => {
    const searchStas = [];
    for (let i = 0; i < selection.length; i += 1) {
      searchStas.push(`${'paymentSessionStatuses'}=${selection[i]}`);
    }
    setSearchStatus(searchStas.toString().replaceAll(',', '&'));
    setSelectedS(selection);
  };

  const onCategoryChange = (selection) => {
    const searchCat = [];
    for (let i = 0; i < selection.length; i += 1) {
      searchCat.push(`${'paymentCategories'}=${selection[i]}`);
    }
    setSearchCats(searchCat.toString().replaceAll(',', '&'));
    setSelectedC(selection);
  };

  const clearMethods = () => {
    setSearchMethos('');
    setSelectedM([]);
  };

  const searchMethods = () => {
    const searchMethod = [];
    for (let i = 0; i < transactionMethods.length; i += 1) {
      searchMethod.push(`${'transactionTypes'}=${transactionMethods[i]}`);
    }
    setSearchMethos(searchMethod.toString().replaceAll(',', '&'));
    setSelectedM(transactionMethods);
  };

  const clearStatus = () => {
    setSearchStatus('');
    setSelectedS([]);
  };

  const searcStatus = () => {
    const searchStas = [];
    for (let i = 0; i < transactionStatus.length; i += 1) {
      searchStas.push(`${'paymentSessionStatuses'}=${transactionStatus[i]}`);
    }
    setSearchStatus(searchStas.toString().replaceAll(',', '&'));
    setSelectedS(transactionStatus);
  };

  const clearCategory = () => {
    setSearchCats('');
    setSelectedC([]);
  };

  const searchCategory = () => {
    const searchCat = [];
    for (let i = 0; i < transactionCategory.length; i += 1) {
      searchCat.push(`${'paymentCategories'}=${transactionCategory[i]}`);
    }
    setSearchCats(searchCat.toString().replaceAll(',', '&'));
    setSelectedC(transactionCategory);
  };

  const handleSearch = (searchText) => {
    setSearchValue(searchText);
    setFilterdRecords(transactionsReport.filter((data) => JSON.stringify(data).toLowerCase().indexOf(searchText.toLowerCase()) !== -1));
  };

  const clearAmounts = () => {
    setSearchAmount('');
  };
  const searchAmounts = (min, max) => {
    if (min && max) {
      setSearchAmount(`minAmount=${Math.round(min * 100)}&maxAmount=${Math.round(max * 100)}`);
    }
    if (min && !max) {
      setSearchAmount(`minAmount=${Math.round(min * 100)}`);
    }
    if (!min && max) {
      setSearchAmount(`maxAmount=${Math.round(min * 100)}`);
    }
    if (!min && !max) {
      setSearchAmount('');
    }
  };

  const clearDates = () => {
    setSearchDate('');
    getData();
  };

  const searchFloatingDate = (floatingType) => {
    const tz = selectedAccount?.timezone;
    let currentEndDate = moment()
      .tz(tz)
      .subtract(15, 'minutes');
    let currentStartDate = moment(currentEndDate)
      .tz(tz)
      .startOf('day');
    let prevEndDate = moment(currentStartDate)
      .tz(tz)
      .subtract(15, 'minutes');
    let prevStartDate = moment(prevEndDate)
      .tz(tz)
      .startOf('day');
    const format = 'YYYY-MM-DDTHH:mm:ss';
    if (floatingType === 'Today') {
      currentEndDate = moment()
        .tz(tz);
      currentStartDate = moment(currentEndDate)
        .tz(tz)
        .startOf('day');
      const end = moment(currentEndDate).utc().format(format);
      const start = moment(currentStartDate).utc().format(format);
      setSearchDate(`transactionDate.AF=${start}&transactionDate.BF=${end}`);
    }
    if (floatingType === 'Day') {
      currentEndDate = moment()
        .tz(tz);
      currentStartDate = moment(currentEndDate)
        .tz(tz)
        .startOf('day');
      prevEndDate = moment(currentStartDate)
        .tz(tz)
        .subtract(15, 'minutes');
      prevStartDate = moment(prevEndDate)
        .tz(tz)
        .startOf('day');
      const end = moment(prevEndDate).utc().format(format);
      const start = moment(prevStartDate).utc().format(format);
      setSearchDate(`transactionDate.AF=${start}&transactionDate.BF=${end}`);
    }
    if (floatingType === '7Days') {
      currentEndDate = moment()
        .tz(tz);
      currentStartDate = moment(currentEndDate)
        .tz(tz)
        .subtract(6, 'day')
        .startOf('day');
      prevEndDate = moment(currentStartDate)
        .tz(tz)
        .subtract(15, 'minutes');
      prevStartDate = moment(prevEndDate)
        .tz(tz)
        .subtract(6, 'day')
        .startOf('day');
      const end = moment(currentEndDate).utc().format(format);
      const start = moment(currentStartDate).utc().format(format);
      setSearchDate(`transactionDate.AF=${start}&transactionDate.BF=${end}`);
    }
    if (floatingType === '30Days') {
      currentEndDate = moment()
        .tz(tz);
      currentStartDate = moment(currentEndDate)
        .tz(tz)
        .subtract(29, 'day')
        .startOf('day');
      prevEndDate = moment(currentStartDate)
        .tz(tz)
        .subtract(15, 'minutes');
      prevStartDate = moment(prevEndDate)
        .tz(tz)
        .subtract(29, 'day')
        .startOf('day');
      const end = moment(currentEndDate).utc().format(format);
      const start = moment(currentStartDate).utc().format(format);
      setSearchDate(`transactionDate.AF=${start}&transactionDate.BF=${end}`);
    }
    if (floatingType === '90Days') {
      currentEndDate = moment()
        .tz(tz);
      currentStartDate = moment(currentEndDate)
        .tz(tz)
        .subtract(3, 'month')
        .startOf('day');
      prevEndDate = moment(currentStartDate)
        .tz(tz)
        .subtract(15, 'minutes');
      prevStartDate = moment(prevEndDate)
        .tz(tz)
        .subtract(3, 'month')
        .startOf('day');
      const end = moment(currentEndDate).utc().format(format);
      const start = moment(currentStartDate).utc().format(format);
      setSearchDate(`transactionDate.AF=${start}&transactionDate.BF=${end}`);
    }
    if (floatingType === 'All') {
      setSearchDate('');
      getData();
    }
  };

  const searchSpecificDate = (startDate, endDate) => {
    const tz = selectedAccount?.timezone;
    const currentEndDate = moment(endDate)
      .tz(tz)
      .endOf('day');
    const currentStartDate = moment(startDate)
      .tz(tz)
      .startOf('day');
    const format = 'YYYY-MM-DDTHH:mm:ss';
    const end = moment(currentEndDate).utc().format(format);
    const start = moment(currentStartDate).utc().format(format);
    setSearchDate(`transactionDate.AF=${start}&transactionDate.BF=${end}`);
  };

  if (loading || !selectedAccount) {
    return <Loading />;
  }

  return (
    <Card
      style={{
        padding: '0',
        borderWidth: '0',
      }}
      className="reporting_summary_card"
    >
      <Row
        type="flex"
        justify="space-between"
        // className="my-2 pb-2"
        className="mb-4 reporting_summary_filter_bar"
        align="middle"
        style={{
          backgroundColor: '#fbfbfb',
          borderBottom: '1px solid #e0e0e0',
          minHeight: '40px',
        }}
      >
        <Col>
          <Row type="flex" justify="start" align="middle">
            <Col className="mr-4 ml-4 "><SpH5>Search By</SpH5></Col>
            <Col>
              {/* <label><SpH5>Date</SpH5></label> */}
              <Input.Group compact placeholder="Date">
                <DateMenu
                  value={selectedM}
                  clearAll={clearDates}
                  searchAll={searchFloatingDate}
                  searchSelected={searchSpecificDate}
                  title="Date"
                />
              </Input.Group>
            </Col>
            <Col>
              {/* <label><SpH5>Amount</SpH5></label> */}
              <Input.Group compact placeholder="Amount">
                <AmountMenu
                  value={selectedM}
                  clearAll={clearAmounts}
                  searchAll={searchAmounts}
                  title="Amount"
                />
              </Input.Group>
            </Col>
            {/* <Col className="mr-5"> */}
            <Col>
              {/* <label><SpH5>Method</SpH5></label> */}
              <Input.Group compact placeholder="Method">
                <CheckboxMenu
                  options={transactionMethods}
                  value={selectedM}
                  onChange={onMethodChange}
                  clearAll={clearMethods}
                  searchAll={searchMethods}
                  title="Method"
                />
              </Input.Group>
            </Col>
            {/* <Col className="mr-5"> */}
            <Col>
              {/* <label><SpH5>Status</SpH5></label> */}
              <Input.Group compact placeholder="Method">
                <CheckboxMenu
                  options={transactionStatus}
                  value={selectedS}
                  onChange={onStatusChange}
                  clearAll={clearStatus}
                  searchAll={searcStatus}
                  title="Status"
                />
              </Input.Group>
            </Col>
            {/* <Col className="mr-5"> */}
            <Col>
              {/* <label><SpH5>Category</SpH5></label> */}
              <Input.Group compact placeholder="Method">
                <CheckboxMenu
                  options={transactionCategory}
                  value={selectedC}
                  onChange={onCategoryChange}
                  clearAll={clearCategory}
                  searchAll={searchCategory}
                  title="Category"
                />
              </Input.Group>
            </Col>
          </Row>
        </Col>
        <Col
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginRight: '10px',
          }}
        >
          <Input.Search
            // className="mr-3 mt-3"
            className="reporting_search_input"
            style={{
              width: 300,
              maxHeight: '35px',
            }}
            value={searchValue}
            placeholder="Search All"
            onChange={(e) => handleSearch((e.target.value))}
          />
        </Col>
      </Row>
      <Table
        columns={columns}
        rowKey={(record) => record.gid}
        dataSource={filterdRecods}
        pagination={false}
        className="mx-4"
      />
    </Card>
  );
};

const mapStateToProps = (state) => ({
  transactionsReport: state?.reportingTransactions?.entity,
  loading: state.loading.loading,
  test: state.account.test,
  totalElements: state.reportingTransactions.totalElements,
  selectedAccount: state.account.selectedAccount,
  submitting: state.loading.submitting,
  validator: state.loading.validator,
  preserveFilters: state.filterParams.preserveFilters,
});

const mapDispatchToProps = (dispatch) => ({
  fetchTransactionsReporting: param => dispatch(reportingTransactions.fetchTransactionsReporting({
    payload: param,
  })),
  setFilterData: (value) => dispatch(filterParamsActions.setFilterData({
    type: FILTERS_SPECS.SET_FILTER_DATA,
    payload: value,
  })),
  setNotification: ({ type, payload }) => dispatch(notificationActions.setNotification({
    type,
    payload,
  })),
});

// $FlowFixMe
export default connect(mapStateToProps, mapDispatchToProps)(TransactinsReporting);
